import React, { useState, useMemo } from "react";
import {
  MessageBar,
  MessageBarType,
  ChoiceGroup,
  IChoiceGroupOption,
  Checkbox,
  getTheme,
  Spinner,
} from "@fluentui/react";
import { mergeStyleSets } from "@fluentui/react";
import {
  Ev2ExperimentStage,
  PipelineRun,
  RolloutAndProgressionType,
} from "../../Types";
import { useToast } from "../../Contexts/ToastContext";
import { postRolloutDetails } from "../../Api/ado.api";
import { cleanRolloutParams } from "./rolloutDetails.utils";
import { useEv2ExperimentParams } from "../../Hooks/UseCurrentExtension";
import { useFetchPipelineId } from "../../Hooks/AdoHooks/UseFetchPipelineId";
import SharedDialog from "./SharedDialog";
import AdvanceDialogManualInstructions from "./RolloutDetailsTable/AdvanceDialogManualInstructions";
import { getExpEnvironment } from "../../Shared/shared.utils";
import {
  RC_TYPE,
  MPAC_TYPE,
  PROD_TYPE,
} from "./RolloutDetailsTable/RolloutDetailsEv2Panel/ev2TreatmentStages.consts";
import useAppInsights from "../../Hooks/UseAppInsights";
import { SeverityLevel } from "@microsoft/applicationinsights-web";

interface AdvanceDialogProps {
  /**
   * Callback used when user dismisses the dialog
   */
  onDismiss: () => void;
  /**
   * Callback used when advancement is successfully submitted
   */
  onAdvanceSubmitted: () => void;
  /**
   * Array of pipeline runs to determine next stage progression
   */
  pipelineRuns: PipelineRun[];
  /**
   * The Ev2 statuses for the experiment's stages
   */
  ev2Statuses: Ev2ExperimentStage[];
  /**
   * The configuration specification filename for the rollout
   */
  configSpecFilename: string;
  /**
   * The latest Ev2 Portal link for the experiment. Used for manual
   * advancement instructions.
   */
  latestEv2PortalLink: string;
  /**
   * When true, shows loading state for EV2 stages
   */
  isStagesLoading: boolean;
}

/**
 * A dialog component for advancing experiment rollouts to the next stage
 * Automatically determines the next stage based on completed pipeline runs and allows configuration of progression type and health checks
 */
const AdvanceDialog: React.FC<AdvanceDialogProps> = ({
  onDismiss,
  onAdvanceSubmitted,
  pipelineRuns,
  ev2Statuses,
  configSpecFilename,
  latestEv2PortalLink,
  isStagesLoading,
}) => {
  const { showToast } = useToast();
  const { extensionFolder } = useEv2ExperimentParams();
  const { trackOperationEvent, trackExceptionEvent, createOperationTracker } =
    useAppInsights("AdvanceDialog");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progressionType, setProgressionType] = useState<string>("automatic");
  const [enableHealthChecks, setEnableHealthChecks] = useState<boolean>(true);
  const { value: pipelineId } = useFetchPipelineId(stageType, setError);

  // Determine the next stage to advance to
  const nextStage = useMemo(() => {
    // Get completed stages from pipeline runs
    const completedStages = new Set<string>();
    ev2Statuses.forEach((stage) => {
      const env = getExpEnvironment(stage.stageName);
      if (stage.status === "Succeeded") {
        completedStages.add(env);
      }
    });

    // Find the most advanced completed stage and determine next stage
    let mostAdvancedIndex = -1;
    for (let i = STAGE_PROGRESSION.length - 1; i >= 0; i--) {
      if (completedStages.has(STAGE_PROGRESSION[i].environment)) {
        mostAdvancedIndex = i;
        break;
      }
    }

    // Return the next stage in progression
    if (
      mostAdvancedIndex >= 0 &&
      mostAdvancedIndex < STAGE_PROGRESSION.length - 1
    ) {
      return STAGE_PROGRESSION[mostAdvancedIndex + 1];
    }

    return null;
  }, [ev2Statuses]);

  // Determine initial health check value from most recent successful rollout
  const initialEnableHealthChecks = useMemo(() => {
    const successfulRuns = pipelineRuns
      .filter((run) => run.result === "succeeded")
      .sort(
        (a, b) =>
          new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime(),
      );

    if (successfulRuns.length > 0) {
      const mostRecentRun = successfulRuns[0];
      // Check if EnableHealthChecks parameter exists and use its value directly
      const enableHealthChecks =
        mostRecentRun.templateParameters?.EnableHealthChecks;

      if (typeof enableHealthChecks === "boolean") {
        return enableHealthChecks;
      }
      // Handle case where it might be stored as a string
      if (enableHealthChecks !== undefined) {
        return String(enableHealthChecks).toLowerCase() === "true";
      }
    }

    // Default to true (health checks enabled)
    return true;
  }, [pipelineRuns]);

  const handleAdvance = async () => {
    if (!nextStage) {
      const error = "No next stage available to advance to.";
      trackExceptionEvent(error, SeverityLevel.Warning, {
        function: "handleAdvance",
      });
      setError(error);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (!pipelineId) {
        throw new Error("No pipeline configured for this extension.");
      }
      const extensionPath = extensionFolder?.extensionRepoPath;
      if (!extensionFolder) {
        throw new Error(
          "Extension data is missing: Unable to find extension path.",
        );
      }
      // Choose the rollout type based on progression selection
      const rolloutType =
        progressionType === "manual"
          ? nextStage.sdpTypeManual
          : nextStage.sdpTypeAuto;

      const rolloutParams = {
        ConfigSpecFileName: configSpecFilename,
        ConfigSpecPath: `Extensions/${extensionPath}`,
        RolloutAndProgressionType: rolloutType,
        ValidateOnly: false,
        EnableHealthChecks: enableHealthChecks,
      };

      const cleanedParams = cleanRolloutParams(rolloutParams);
      const tracker = createOperationTracker("Advance", "handleAdvance");
      tracker.start({
        pipelineId,
        params: cleanedParams,
      });

      try {
        await postRolloutDetails(pipelineId, cleanedParams);
        tracker.succeed({ environment: nextStage.environment });
      } catch (err: any) {
        tracker.fail(err.message, { environment: nextStage.environment });
        throw err;
      }

      showToast({
        message: `Advanced to ${nextStage.environment} stage (${progressionType}) successfully!`,
        type: "success",
        dismissible: true,
        duration: 3000,
      });

      onAdvanceSubmitted();
      onDismiss();
    } catch (err: any) {
      setError(err.message || "Failed to advance to next stage");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset error and progression type when dialog opens
  React.useEffect(() => {
    setError(null);
    setProgressionType("automatic"); // Reset to default
    setEnableHealthChecks(initialEnableHealthChecks); // Initialize with value from most recent rollout
  }, [initialEnableHealthChecks]);

  const isManual = progressionType === "manual";

  return (
    <SharedDialog
      isOpen={true}
      onDismiss={onDismiss}
      onConfirm={handleAdvance}
      title="Advance to Next Stage"
      subText={
        nextStage
          ? `This action will create a new rollout in the ${nextStage.environment} environment. Select the progression type below.`
          : "No next stage available for advancement."
      }
      isSubmitting={isSubmitting}
      confirmButtonDisabled={!nextStage || isManual}
      error={error}
    >
      {isStagesLoading ? (
        <Spinner label="Loading stage information..." />
      ) : nextStage ? (
        <div className={styles.stageInfo}>
          <div className={styles.progressionOptions}>
            <div className={styles.sectionHeader}>Progression Type:</div>
            <ChoiceGroup
              options={progressionOptions}
              selectedKey={progressionType}
              onChange={(_, option) => {
                if (option) {
                  setProgressionType(option.key);
                  trackOperationEvent({
                    operation: "FormChange",
                    function: "ChoiceGroup.onChange",
                    properties: {
                      value: option.key,
                      field: "ProgressionType",
                      initialValue: progressionType,
                    },
                  });
                }
              }}
            />
          </div>
          {isManual ? (
            <AdvanceDialogManualInstructions
              latestEv2PortalLink={latestEv2PortalLink}
            />
          ) : (
            <>
              <div className={styles.healthCheckOptions}>
                <div className={styles.sectionHeader}>
                  Health Check Options:
                </div>
                <Checkbox
                  label="Enable Health Checks"
                  checked={enableHealthChecks}
                  onChange={(_, checked) => {
                    setEnableHealthChecks(!!checked);
                    trackOperationEvent({
                      operation: "FormChange",
                      function: "Checkbox.onChange",
                      properties: {
                        field: "enableHealthChecks",
                        value: !!checked,
                        initialValue: enableHealthChecks,
                      },
                    });
                  }}
                />
              </div>
              <div className={styles.advanceMessage}>
                <strong>Next Stage:</strong> {nextStage.environment} (
                {progressionType === "manual"
                  ? nextStage.sdpTypeManual
                  : nextStage.sdpTypeAuto}
                )
                {!enableHealthChecks && (
                  <div
                    style={{
                      color: theme.semanticColors.errorText,
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    Health checks will be skipped
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      ) : (
        <div className={styles.noAdvancement}>
          <MessageBar messageBarType={MessageBarType.info}>
            No advancement available. Either all stages are complete or no
            stages have been successfully completed yet.
          </MessageBar>
        </div>
      )}
    </SharedDialog>
  );
};

// Progression stages in order
const STAGE_PROGRESSION = [
  {
    environment: RC_TYPE,
    sdpTypeAuto: RolloutAndProgressionType.RC_SDP,
    sdpTypeManual: RolloutAndProgressionType.RC_SDP_MANUAL,
  },
  {
    environment: MPAC_TYPE,
    sdpTypeAuto: RolloutAndProgressionType.MPAC_SDP,
    sdpTypeManual: RolloutAndProgressionType.MPAC_SDP_MANUAL,
  },
  {
    environment: PROD_TYPE,
    sdpTypeAuto: RolloutAndProgressionType.PROD_SDP,
    sdpTypeManual: RolloutAndProgressionType.PROD_SDP_MANUAL,
  },
];

const progressionOptions: IChoiceGroupOption[] = [
  { key: "automatic", text: "Automatic" },
  { key: "manual", text: "Manual" },
];

const stageType = "single";

const theme = getTheme();
const styles = mergeStyleSets({
  stageInfo: {
    marginBottom: "16px",
  },
  advanceMessage: {
    fontSize: "16px",
    marginBottom: "12px",
    padding: "12px",
    backgroundColor: theme.semanticColors.bodyBackgroundHovered,
    border: `1px solid ${theme.semanticColors.variantBorder}`,
    borderRadius: "4px",
  },
  confirmationText: {
    fontSize: "14px",
    color: theme.semanticColors.bodySubtext,
    lineHeight: "1.4",
    marginBottom: "16px",
  },
  progressionOptions: {
    marginBottom: "16px",
  },
  healthCheckOptions: {
    marginBottom: "16px",
  },
  sectionHeader: {
    fontWeight: "600",
    marginBottom: "12px",
    fontSize: "14px",
  },
  noAdvancement: {
    marginBottom: "16px",
  },
});

export default AdvanceDialog;
